"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildAgentChartOption = void 0;
const chart_utils_1 = require("./chart-utils");
const options_1 = require("./options");
const SINGLE_HORIZONTAL_CATEGORY_GAP = '35%';
const GROUPED_HORIZONTAL_CATEGORY_GAP = '25%';
const DEFAULT_HORIZONTAL_BAR_WIDTH = 35;
const DEFAULT_VERTICAL_BAR_WIDTH = 40;
const GROUPED_HORIZONTAL_MIN_WIDTH = 18;
const GROUPED_HORIZONTAL_WIDTH_STEP = 6;
const DEFAULT_DONUT_INNER_RADIUS = 0.45;
const DEFAULT_DONUT_OUTER_RADIUS = '70%';
const DONUT_HOLE_CLAMP_MIN = 0.9;
const buildLineOrAreaOption = (chart, normalized) => {
    var _a;
    const { categories, options, series } = normalized;
    const animationEnabled = options.animate !== false;
    const resolvedSeries = series.map((s) => ({ data: s.data, name: s.name }));
    const colors = (0, chart_utils_1.resolveColorPalette)(series.length, options.colors);
    const userSmooth = options.smooth;
    const smoothInput = userSmooth !== undefined ? userSmooth : options.curve ? undefined : chart_utils_1.DEFAULT_LINE_SMOOTHNESS;
    const strokeWidth = typeof options.lineWidth === 'number' ? options.lineWidth : chart_utils_1.DEFAULT_LINE_STROKE_WIDTH;
    const pointSize = (0, chart_utils_1.clampPointSize)(options.pointSize);
    const allowPoints = (_a = options.showPoints) !== null && _a !== void 0 ? _a : true;
    const showSymbols = allowPoints && pointSize > 0;
    const gridOpacity = (0, chart_utils_1.clampUnit)(options.gridOpacity);
    const legend = (0, chart_utils_1.buildLegendConfig)(options, series.length > 1);
    const tooltip = {
        appendToBody: true,
        confine: true,
        trigger: 'axis'
    };
    if (chart.type === 'area') {
        const areaOpacity = typeof options.areaOpacity === 'number' ? options.areaOpacity : chart_utils_1.DEFAULT_AREA_OPACITY;
        const areaOption = (0, options_1.buildAreaChartOption)({
            animation: animationEnabled,
            areaOpacity,
            categories,
            colors,
            curve: options.curve,
            grid: { left: 20, right: 20 },
            gridOpacity,
            series: resolvedSeries,
            showDataLabels: options.showDataLabels,
            showSymbol: showSymbols,
            smooth: smoothInput,
            stacked: options.stacked,
            strokeWidth,
            symbolSize: pointSize,
            tooltip,
            yAxisName: options.yAxisLabel
        });
        areaOption.legend = legend;
        return areaOption;
    }
    const lineOption = (0, options_1.buildLineChartOption)({
        animation: animationEnabled,
        categories,
        colors,
        curve: options.curve,
        grid: { left: 20, right: 20 },
        gridOpacity,
        series: resolvedSeries,
        showDataLabels: options.showDataLabels,
        showSymbol: showSymbols,
        smooth: smoothInput,
        stacked: options.stacked,
        strokeWidth,
        symbolSize: pointSize,
        tooltip,
        yAxisName: options.yAxisLabel
    });
    lineOption.legend = legend;
    return lineOption;
};
const buildBarOption = (chart, normalized) => {
    var _a, _b, _c;
    const { categories, options, series } = normalized;
    const animationEnabled = options.animate !== false;
    const isHorizontal = chart.type === 'horizontal-bar';
    const isStacked = (_a = options.stacked) !== null && _a !== void 0 ? _a : false;
    const borderRadius = (_b = options.borderRadius) !== null && _b !== void 0 ? _b : chart_utils_1.FALLBACK_BAR_BORDER_RADIUS;
    const valueAxisGridOpacity = (0, chart_utils_1.clampUnit)(options.gridOpacity);
    const barWidthPercent = typeof options.barPadding === 'number' ? `${Math.round((1 - options.barPadding) * 100)}%` : undefined;
    const computeHorizontalBarWidth = () => {
        if (isStacked || series.length <= 1) {
            return DEFAULT_HORIZONTAL_BAR_WIDTH;
        }
        const widthReduction = (series.length - 1) * GROUPED_HORIZONTAL_WIDTH_STEP;
        const computed = DEFAULT_HORIZONTAL_BAR_WIDTH - widthReduction;
        return Math.max(GROUPED_HORIZONTAL_MIN_WIDTH, computed);
    };
    let builtSeries = [];
    let colors = [];
    if (isStacked) {
        colors = (0, chart_utils_1.resolveColorPalette)(series.length, options.colors);
        const terminalSeriesByCategory = categories.map((_, catIdx) => {
            var _a;
            for (let seriesIdx = series.length - 1; seriesIdx >= 0; seriesIdx -= 1) {
                const value = (_a = series[seriesIdx].data[catIdx]) !== null && _a !== void 0 ? _a : 0;
                if (value && value > 0) {
                    return seriesIdx;
                }
            }
            return -1;
        });
        const terminalRadius = isHorizontal ? [0, borderRadius, borderRadius, 0] : [borderRadius, borderRadius, 0, 0];
        builtSeries = series.map((s, seriesIdx) => {
            const dataPoints = categories.map((_, catIdx) => {
                var _a;
                const value = (_a = s.data[catIdx]) !== null && _a !== void 0 ? _a : 0;
                const isTerminal = terminalSeriesByCategory[catIdx] === seriesIdx;
                return {
                    itemStyle: {
                        borderRadius: isTerminal ? terminalRadius : 0,
                        color: colors[seriesIdx % colors.length]
                    },
                    value
                };
            });
            return {
                data: dataPoints,
                name: s.name,
                stack: 'total'
            };
        });
    }
    else if (series.length === 1) {
        const totalBars = categories.length;
        colors = (0, chart_utils_1.resolveColorPalette)(totalBars, options.colors);
        builtSeries = series.map((s) => {
            const dataPoints = categories.map((_, catIdx) => {
                var _a;
                return ({
                    itemStyle: {
                        borderRadius: isHorizontal
                            ? [0, borderRadius, borderRadius, 0]
                            : [borderRadius, borderRadius, 0, 0],
                        color: colors[catIdx % colors.length]
                    },
                    value: (_a = s.data[catIdx]) !== null && _a !== void 0 ? _a : 0
                });
            });
            return {
                data: dataPoints,
                name: s.name
            };
        });
    }
    else {
        colors = (0, chart_utils_1.resolveColorPalette)(series.length, options.colors);
        builtSeries = series.map((s, seriesIdx) => ({
            data: categories.map((_, catIdx) => {
                var _a;
                return ({
                    itemStyle: {
                        borderRadius: isHorizontal ? [0, borderRadius, borderRadius, 0] : [borderRadius, borderRadius, 0, 0]
                    },
                    value: (_a = s.data[catIdx]) !== null && _a !== void 0 ? _a : 0
                });
            }),
            itemStyle: {
                color: colors[seriesIdx % colors.length]
            },
            name: s.name
        }));
    }
    const optionResult = (0, options_1.buildGroupedBarOption)({
        categories,
        colors,
        grid: { left: 20, right: 20 },
        layout: isHorizontal ? 'horizontal' : 'vertical',
        series: builtSeries,
        showLabels: (_c = options.showDataLabels) !== null && _c !== void 0 ? _c : true,
        tooltip: {
            appendToBody: true,
            axisPointer: { type: 'shadow' },
            confine: true,
            trigger: 'axis'
        },
        valueAxisGridOpacity
    });
    optionResult.animation = animationEnabled;
    optionResult.animationDuration = animationEnabled ? chart_utils_1.DEFAULT_ANIMATION_DURATION : 0;
    if (!isHorizontal && optionResult.xAxis) {
        optionResult.xAxis.axisLabel = Object.assign(Object.assign({}, optionResult.xAxis.axisLabel), { interval: 0 });
    }
    const resolvedBarWidth = barWidthPercent || (isHorizontal ? computeHorizontalBarWidth() : DEFAULT_VERTICAL_BAR_WIDTH);
    const shouldApplySingleHorizontalGap = isHorizontal && !isStacked && series.length === 1;
    const shouldApplyGroupedHorizontalGap = isHorizontal && !isStacked && series.length > 1;
    optionResult.series = optionResult.series.map((s) => (Object.assign(Object.assign({}, s), { barCategoryGap: shouldApplySingleHorizontalGap
            ? SINGLE_HORIZONTAL_CATEGORY_GAP
            : shouldApplyGroupedHorizontalGap
                ? GROUPED_HORIZONTAL_CATEGORY_GAP
                : s.barCategoryGap, barWidth: resolvedBarWidth })));
    optionResult.legend = (0, chart_utils_1.buildLegendConfig)(options, series.length > 1);
    if (options.yAxisLabel) {
        if (isHorizontal) {
            optionResult.xAxis = Object.assign(Object.assign({}, (optionResult.xAxis || {})), { name: options.yAxisLabel });
        }
        else {
            optionResult.yAxis = Object.assign(Object.assign({}, (optionResult.yAxis || {})), { name: options.yAxisLabel });
        }
    }
    return optionResult;
};
const clampDonutHole = (value) => Math.min(DONUT_HOLE_CLAMP_MIN, Math.max(0, value));
const buildPieOrDonutOption = (chart, normalized) => {
    var _a, _b, _c;
    const { categories, options, series } = normalized;
    const animationEnabled = options.animate !== false;
    const data = categories.map((cat, idx) => {
        var _a, _b;
        return ({
            name: cat,
            value: (_b = (_a = series[0]) === null || _a === void 0 ? void 0 : _a.data[idx]) !== null && _b !== void 0 ? _b : 0
        });
    });
    const colors = (0, chart_utils_1.resolveColorPalette)(categories.length, options.colors);
    const legend = (0, chart_utils_1.buildLegendConfig)(options, true);
    const optionResult = (0, options_1.buildDonutChartOption)({
        colors,
        data,
        legend,
        tooltip: {
            appendToBody: true,
            confine: true,
            formatter: '{b}: {c} ({d}%)',
            trigger: 'item'
        }
    });
    optionResult.animation = animationEnabled;
    optionResult.animationDuration = animationEnabled ? chart_utils_1.DEFAULT_ANIMATION_DURATION : 0;
    optionResult.legend = legend;
    let innerRadius;
    if (typeof options.donutHoleSize === 'number') {
        innerRadius = `${clampDonutHole(options.donutHoleSize) * 100}%`;
    }
    else if (chart.type === 'pie') {
        innerRadius = '0%';
    }
    else {
        innerRadius = `${DEFAULT_DONUT_INNER_RADIUS * 100}%`;
    }
    const slicePadding = (0, chart_utils_1.clampSlicePadding)(options.slicePadding);
    optionResult.series = [
        Object.assign(Object.assign(Object.assign(Object.assign({}, optionResult.series[0]), { label: Object.assign(Object.assign({}, ((_b = (_a = optionResult.series[0]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : {})), { show: (_c = options.showDataLabels) !== null && _c !== void 0 ? _c : true }) }), (slicePadding !== undefined ? { padAngle: slicePadding } : {})), { radius: [innerRadius, DEFAULT_DONUT_OUTER_RADIUS] })
    ];
    return optionResult;
};
const buildAgentChartOption = (chart) => {
    const normalized = (0, chart_utils_1.normalizeChartData)(chart);
    if (chart.type === 'line' || chart.type === 'area') {
        return buildLineOrAreaOption(chart, normalized);
    }
    if (chart.type === 'bar' || chart.type === 'horizontal-bar') {
        return buildBarOption(chart, normalized);
    }
    if (chart.type === 'pie' || chart.type === 'donut') {
        return buildPieOrDonutOption(chart, normalized);
    }
    return {};
};
exports.buildAgentChartOption = buildAgentChartOption;
//# sourceMappingURL=chart-option-builders.js.map